import { useCallback, useState, useEffect } from 'react'

import { useMutation } from '~/hooks/useMutation'
import type { Product } from '~/types/Product'
import type { Color } from '~/types/Color'
import type { Design } from '~/types/Design'
import type { Customization } from '~/types/Customization'
import {
  generateCustomization,
  type AICustomizationResponse,
  type ExtendedCustomizationContext
} from '~/api/ai'
import { parseAIResponseToCustomization } from '~/utils/ai/parse'
import { saveToHistory } from '~/utils/ai/history'

interface UseAICustomizationParams {
  product: Product
  colors: readonly Color[]
  design: Design
  currentCustomization: Customization
}

import type { SuggestionWithCustomization } from '~/components/organisms/AICustomization/types'

interface UseAICustomizationResult {
  generateCustomization: (
    prompt: string,
    selectedColorIds?: string[]
  ) => Promise<void>
  isGenerating: boolean
  error: Error | null
  suggestions: SuggestionWithCustomization[] | null
  clearError: () => void
}

export function useAICustomization({
  product,
  colors,
  design,
  currentCustomization
}: UseAICustomizationParams): UseAICustomizationResult {
  const [suggestions, setSuggestions] = useState<
    SuggestionWithCustomization[] | null
  >(null)
  const [lastProcessedData, setLastProcessedData] =
    useState<AICustomizationResponse | null>(null)

  const mutation = useMutation<
    AICustomizationResponse,
    { prompt: string; selectedColorIds?: string[] },
    Error
  >(async ({ prompt, selectedColorIds }, signal: AbortSignal) => {
    const context: ExtendedCustomizationContext = {
      availableColors: colors,
      design,
      selectedColorIds: selectedColorIds || [],
      allColors: colors
    }

    const response = await generateCustomization(prompt, context)

    // Save to history
    saveToHistory(prompt, response)

    return response
  })

  const generate = useCallback(
    async (prompt: string, selectedColorIds?: string[]) => {
      setSuggestions(null)

      mutation.mutate({ prompt, selectedColorIds })
    },
    [mutation]
  )

  // Handle mutation results
  useEffect(() => {
    if (mutation.status === 'success' && mutation.data !== lastProcessedData) {
      // Convert single suggestion to include parsed customization
      const s = mutation.data.suggestion
      const suggestionWithCustomization = {
        ...s,
        customization: parseAIResponseToCustomization(
          {
            parts: s.parts,
            reasoning: s.reasoning,
            printLogo: s.printLogo
          },
          currentCustomization
        )
      }
      setSuggestions([suggestionWithCustomization])
      setLastProcessedData(mutation.data)
    }
  }, [mutation, currentCustomization, lastProcessedData])

  const clearError = useCallback(() => {
    mutation.clear()
  }, [mutation])

  return {
    generateCustomization: generate,
    isGenerating: mutation.status === 'loading',
    error: mutation.status === 'failed' ? mutation.error : null,
    suggestions,
    clearError
  }
}
